import React from 'react';
import { ArrowDownUp, Eye, Heart, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type SortOption = 'recent' | 'oldest' | 'price_asc' | 'price_desc' | 'area_desc' | 'area_asc';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigos' },
  { value: 'price_asc', label: 'Menor preço' },
  { value: 'price_desc', label: 'Maior preço' },
  { value: 'area_desc', label: 'Maior área' },
  { value: 'area_asc', label: 'Menor área' },
];

interface SearchResultsHeaderProps {
  totalResults: number;
  location?: string;
  sortValue?: SortOption;
  onSortChange?: (value: SortOption) => void;
  showMapButton?: boolean;
  onMapClick?: () => void;
  mapLabel?: string;
  className?: string;
  // Favoritos props
  showFavoritesButton?: boolean;
  favoritesCount?: number;
  showOnlyFavorites?: boolean;
  onFavoritesClick?: () => void;
}

const SearchResultsHeader: React.FC<SearchResultsHeaderProps> = ({
  totalResults,
  location,
  sortValue = 'recent',
  onSortChange,
  showMapButton = false,
  onMapClick,
  mapLabel = "Mostrar mapa",
  className,
  showFavoritesButton = false,
  favoritesCount = 0,
  showOnlyFavorites = false,
  onFavoritesClick
}) => {
  const currentSortLabel = SORT_OPTIONS.find(opt => opt.value === sortValue)?.label || 'Ordenar por';

  return (
    <div className={cn(
      "flex flex-col lg:flex-row h-auto lg:h-[61px] py-4 gap-4 lg:gap-0 lg:justify-between lg:items-center w-full",
      className
    )}>
      {/* Results Info */}
      <div className="flex flex-col justify-center items-start gap-2">
        <h1 className="text-xl lg:text-2xl font-black text-neutral-black">
          {totalResults} {totalResults === 1 ? 'imóvel disponível' : 'imóveis disponíveis'}
        </h1>
        {location && (
          <p className="text-lg lg:text-xl font-normal text-gray-medium">
            em {location}
          </p>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end items-center gap-3 lg:gap-4 w-full lg:w-auto">
        {/* Favorites Button */}
        {showFavoritesButton && (
          <button
            onClick={onFavoritesClick}
            disabled={favoritesCount === 0}
            className={cn(
              "flex items-center gap-2 h-10 px-3 lg:px-5 rounded-full",
              "font-medium text-sm lg:text-base transition-all duration-200",
              "border-2 relative",
              showOnlyFavorites
                ? "bg-red-50 border-red-500 text-red-600 hover:bg-red-100"
                : favoritesCount > 0
                ? "bg-white border-gray-300 text-gray-700 hover:border-red-500 hover:text-red-600"
                : "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed",
            )}
            title={favoritesCount === 0 ? "Nenhum favorito salvo" : showOnlyFavorites ? "Mostrar todos os imóveis" : "Filtrar meus favoritos"}
          >
            <Heart
              className={cn(
                "w-5 h-5 transition-all",
                showOnlyFavorites && "fill-red-500"
              )}
            />
            {/* Mobile: apenas contador */}
            <span className="sm:hidden">
              {favoritesCount > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">
                  {favoritesCount}
                </span>
              )}
            </span>
            {/* Tablet: texto curto + contador */}
            <span className="hidden sm:inline lg:hidden">
              Favoritos
            </span>
            {/* Desktop: texto completo + contador */}
            <span className="hidden lg:inline">
              {showOnlyFavorites ? "Mostrando Favoritos" : "Meus Favoritos"}
            </span>
            {favoritesCount > 0 && (
              <span className="hidden sm:inline ml-1 px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">
                {favoritesCount}
              </span>
            )}
          </button>
        )}

        {/* Sort Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="flex h-10 px-4 lg:px-5 items-center gap-2 rounded-full border border-gray-text"
            >
              <span className="text-sm lg:text-base font-normal text-neutral-black">
                {currentSortLabel}
              </span>
              <ArrowDownUp className="w-5 lg:w-6 h-5 lg:h-6 text-neutral-gray-dark" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 bg-white z-50">
            {SORT_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => onSortChange?.(option.value)}
                className="flex items-center justify-between cursor-pointer"
              >
                <span>{option.label}</span>
                {sortValue === option.value && (
                  <Check className="w-4 h-4 text-primary" />
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Map Button - Apenas Desktop */}
        {showMapButton && (
          <Button
            variant="primary"
            onClick={onMapClick}
            className="hidden lg:flex h-10 px-6 lg:px-8 justify-center items-center gap-2 rounded-full"
          >
            <span className="text-sm lg:text-base font-normal">
              {mapLabel}
            </span>
            <Eye className="w-5 lg:w-6 h-5 lg:h-6" />
          </Button>
        )}
      </div>
    </div>
  );
};

export { SearchResultsHeader, type SearchResultsHeaderProps };
export default SearchResultsHeader;